<template>
  <block-item-card
    :title="title || $t('EVE.Section.RegulatoryThresholds.Title')"
    :tooltip="$t('EVE.Section.RegulatoryThresholds.Tooltip')"
    class="fill-height"
    :style="dashboardView ? 'border:none;': ''"
    :override-title-size="dashboardView"
    :title-size="12"
    :title-icon="titleIcon"
    :title-icon-size="titleIconSize"
  >
    <v-row class="ma-0 pa-0">
      <v-col
        class="px-1 py-1"
        cols="12"
      >
        <div :class="dashboardView ? 'pa-4 py-0' : 'pa-4'">
          <v-lazy
            :options="{threshold: .5}"
            transition="fade-transition"
          >
            <e-chart
              :option="chartOptions"
              class="chart"
              style="width: 100%; height: 600px;"
              renderer="canvas"
              :autoresize="true"
            />
          </v-lazy>
        </div>
      </v-col>
    </v-row>
  </block-item-card>
</template>

<script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import { use } from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, MarkLineComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
use([BarChart, GridComponent, TooltipComponent, MarkLineComponent, CanvasRenderer])

export default {
  name      : 'RegulatoryThresholds',
  components: { BlockItemCard },
  mixins    : [chartsColorMixin],
  props     : {
    data: {
      type   : Object,
      default: () => null
    },
    title: {
      type   : String,
      default: ''
    },
    dashboardView: {
      type   : Boolean,
      default: false
    },
    titleIcon: {
      type   : String,
      default: ''
    },
    titleIconSize: {
      type   : [String, Number],
      default: 10
    }
  },
  data () {
    return {
      chart: null
    }
  },
  computed: {
    chartData () {
      if (!this.data || !this.data.result || !this.data.result.regulatoryThresholds) {
        // Fallback to hardcoded data for testing
        return {
          categories: ['Basel • 1', 'Basel • 2', 'Basel • 3', 'Basel • 4', 'Basel • 5', 'Basel • 6'],
          values    : [-8, 9, -2, -2, -5, 5],
          thresholds: {
            baselStrictUpper    : 15,
            federalGuidanceUpper: 10,
            federalGuidanceLower: -10,
            baselStrictLower    : -15
          }
        }
      }

      const regulatoryData = this.data.result.regulatoryThresholds

      // Extract categories and values from the API data
      const categories = (regulatoryData.items || []).map(item => (item.scenario && item.scenario.baselTitle) || 'Unknown')
      const values = (regulatoryData.items || []).map(item => item.factor || 0)

      const thresholds = {}

      // If thresholds are available in the API data, use them
      if (regulatoryData.thresholds) {
        regulatoryData.thresholds.forEach(threshold => {
          if (threshold.title && threshold.title.toLowerCase().includes('basel strict')) {
            if (threshold.threshold > 0) {
              thresholds.baselStrictUpper = threshold.threshold
            } else {
              thresholds.baselStrictLower = threshold.threshold
            }
          } else if (threshold.title && threshold.title.toLowerCase().includes('federal guidance')) {
            if (threshold.threshold > 0) {
              thresholds.federalGuidanceUpper = threshold.threshold
            } else {
              thresholds.federalGuidanceLower = threshold.threshold
            }
          }
        })
      }

      return {
        categories,
        values,
        thresholds
      }
    },
    chartOptions () {
      return {
        animation        : true,
        animationDuration: 800,
        title            : {
          show: false
        },
        tooltip: {
          show       : true,
          trigger    : 'axis',
          axisPointer: {
            type: 'shadow'
          },
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : function (params) {
            if (params && params.length > 0) {
              // Find the main data series (Basel Test Results)
              const mainData = params.find(param => param.seriesName === 'Basel Test Results')
              if (mainData) {
                return `${mainData.name}<br/>Value: ${mainData.value}%`
              }
              // Fallback to first parameter if main series not found
              const data = params[0]
              return `${data.name}<br/>Value: ${data.value}%`
            }
            return ''
          }
        },
        grid: {
          left           : '0',
          right          : '170',
          bottom         : '5%',
          top            : '7%',
          containLabel   : true,
          backgroundColor: {
            type      : 'linear',
            x         : 0,
            y         : 0,
            x2        : 0,
            y2        : 1,
            colorStops: [
              {
                offset: 0,
                color : this.themeColorRGBA('synthesis-ui-red-01', 0.1)
              },
              {
                offset: 0.25,
                color : this.themeColorRGBA('synthesis-ui-red-01', 0.05)
              },
              {
                offset: 0.5,
                color : this.themeColorRGBA('synthesis-brand-white', 0.1)
              },
              {
                offset: 0.75,
                color : this.themeColorRGBA('synthesis-ui-red-01', 0.05)
              },
              {
                offset: 1,
                color : this.themeColorRGBA('synthesis-ui-red-01', 0.1)
              }
            ]
          }
        },
        xAxis: {
          type    : 'category',
          data    : this.chartData.categories,
          axisLine: {
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06')
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            show: false
          }
        },
        yAxis: {
          type    : 'value',
          min     : this.getMinValue(),
          max     : this.getMaxValue(),
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color    : this.themeColorRGBA('synthesis-ui-grey-01'),
            formatter: '{value}%'
          },
          splitLine: {
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02'),
              type : 'dashed'
            }
          }
        },
        series: [
          {
            name     : 'Gradient Background',
            type     : 'bar',
            stack    : 'gradient',
            data     : this.chartData.values.map(() => this.chartData.thresholds.baselStrictUpper),
            barWidth : '100%',
            itemStyle: {
              color: {
                type      : 'linear',
                x         : 0,
                y         : 0,
                x2        : 0,
                y2        : 1,
                colorStops: [
                  {
                    offset: 0,
                    color : this.themeColorRGBA('synthesis-ui-red-08', 0.6)
                  },
                  {
                    offset: 0.33,
                    color : this.themeColorRGBA('synthesis-ui-red-08', 0.1)
                  },
                  {
                    offset: 0.5,
                    color : this.themeColorRGBA('synthesis-brand-white', 0.1)
                  },
                  {
                    offset: 0.67,
                    color : 'white'
                  },
                  {
                    offset: 1,
                    color : 'white'
                  }
                ]
              }
            },
            silent        : true,
            z             : 0,
            barGap        : '-100%',
            barCategoryGap: '20%'
          },
          {
            name     : 'Gradient Background Negative',
            type     : 'bar',
            stack    : 'gradient',
            data     : this.chartData.values.map(() => this.chartData.thresholds.baselStrictLower),
            barWidth : '100%',
            itemStyle: {
              color: {
                type      : 'linear',
                x         : 0,
                y         : 0,
                x2        : 0,
                y2        : 1,
                colorStops: [
                  {
                    offset: 0,
                    color : 'white'
                  },
                  {
                    offset: 0.33,
                    color : 'white'
                  },
                  {
                    offset: 0.5,
                    color : this.themeColorRGBA('synthesis-brand-white', 0.1)
                  },
                  {
                    offset: 0.67,
                    color : this.themeColorRGBA('synthesis-ui-red-08', 0.1)
                  },
                  {
                    offset: 1,
                    color : this.themeColorRGBA('synthesis-ui-red-08', 0.6)
                  }
                ]
              }
            },
            silent        : true,
            z             : 0,
            barGap        : '-100%',
            barCategoryGap: '20%'
          },
          {
            name: 'Background',
            type: 'bar',
            data: this.chartData.values.map((value) => {
              // Grey boxes show the full range to Basel Strict threshold
              if (value >= 0) {
                // For positive values: from 0 to +15%
                return this.chartData.thresholds.baselStrictUpper
              } else {
                // For negative values: from 0 to -15%
                return this.chartData.thresholds.baselStrictLower
              }
            }),
            barWidth : '40%',
            itemStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 0.3)
            },
            silent        : true,
            z             : 1,
            barGap        : '-100%',
            barCategoryGap: '20%'
          },
          {
            name: 'Basel Test Results',
            type: 'bar',
            data: this.chartData.values.map((value, index) => ({
              value    : value,
              itemStyle: {
                color       : this.themeColorRGBA('synthesis-ui-blue-04'),
                borderRadius: [4, 4, 4, 4]
              }
            })),
            barWidth      : '40%',
            barGap        : '-100%',
            barCategoryGap: '20%',
            label         : {
              show     : true,
              // position: function (params) {
              //   return params.value >= 0 ? 'top' : 'bottom'
              // },
              // formatter: function (params) {
              //   const category = this.chartData.categories[params.dataIndex] || ''
              //   const value = params.value + '%'
              //   return `${category} ${value}`
              // }.bind(this),
              position : 'outside',
              formatter: function (params) {
                return params.value + '%'
              },
              color        : this.themeColorRGBA('synthesis-ui-grey-01'),
              fontSize     : 11,
              fontWeight   : 'bold',
              // offset    : [0, -15]
              align        : 'center',
              verticalAlign: 'middle',
              distance     : 15
            },
            markLine: {
              silent: true,
              symbol: 'none', // Remove arrows
              data  : [
                {
                  yAxis    : this.chartData.thresholds.baselStrictUpper,
                  lineStyle: {
                    color: this.themeColorRGBA('synthesis-ui-red-01'),
                    type : 'dashed',
                    width: 1.051
                  },
                  label: {
                    show           : true,
                    position       : 'end',
                    formatter      : '{a|Basel Strict Threshold}',
                    width          : 141,
                    height         : 24,
                    color          : this.themeColorRGBA('synthesis-ui-red-01'),
                    fontSize       : 12,
                    backgroundColor: this.themeColorRGBA('synthesis-brand-white'),
                    borderColor    : 'transparent',
                    borderRadius   : 4,
                    padding        : [4, 8, 4, 8],
                    shadowColor    : 'rgba(110, 142, 223, 0.32)',
                    shadowOffsetX  : 4,
                    shadowOffsetY  : -4,
                    shadowBlur     : 12,
                    shadowSpread   : 0,
                    rich           : {
                      a: {
                        color        : this.themeColorRGBA('synthesis-ui-red-01'),
                        fontSize     : 12,
                        align        : 'center',
                        verticalAlign: 'middle',
                        height       : 24
                      }
                    }
                  }
                },
                {
                  yAxis    : this.chartData.thresholds.federalGuidanceUpper,
                  lineStyle: {
                    color: this.themeColorRGBA('synthesis-ui-green-01'),
                    type : 'dashed',
                    width: 1.051
                  },
                  label: {
                    show           : true,
                    position       : 'end',
                    formatter      : '{a|Federal Guidance}',
                    width          : 141,
                    height         : 24,
                    color          : this.themeColorRGBA('synthesis-ui-green-01'),
                    fontSize       : 12,
                    backgroundColor: this.themeColorRGBA('synthesis-brand-white'),
                    borderColor    : 'transparent',
                    shadowBlur     : 12,
                    shadowSpread   : 0,
                    borderRadius   : 4,
                    padding        : [4, 8, 4, 8],
                    shadowColor    : 'rgba(110, 142, 223, 0.32)',
                    shadowOffsetX  : 4,
                    shadowOffsetY  : 1,
                    rich           : {
                      a: {
                        color        : this.themeColorRGBA('synthesis-ui-green-01'),
                        fontSize     : 12,
                        align        : 'center',
                        verticalAlign: 'middle',
                        height       : 24
                      }
                    }
                  }
                },
                {
                  yAxis    : this.chartData.thresholds.federalGuidanceLower,
                  lineStyle: {
                    color: this.themeColorRGBA('synthesis-ui-green-01'),
                    type : 'dashed',
                    width: 1.051
                  },
                  label: {
                    show           : true,
                    position       : 'end',
                    formatter      : '{a|Federal Guidance}',
                    width          : 141,
                    height         : 24,
                    color          : this.themeColorRGBA('synthesis-ui-green-01'),
                    fontSize       : 12,
                    backgroundColor: this.themeColorRGBA('synthesis-brand-white'),
                    borderColor    : 'transparent',
                    borderRadius   : 4,
                    padding        : [4, 8, 4, 8],
                    shadowBlur     : 12,
                    shadowSpread   : 0,
                    shadowColor    : 'rgba(110, 142, 223, 0.32)',
                    shadowOffsetX  : 4,
                    shadowOffsetY  : 4,
                    rich           : {
                      a: {
                        color        : this.themeColorRGBA('synthesis-ui-green-01'),
                        fontSize     : 12,
                        align        : 'center',
                        verticalAlign: 'middle',
                        height       : 24
                      }
                    }
                  }
                },
                {
                  yAxis    : this.chartData.thresholds.baselStrictLower,
                  lineStyle: {
                    color: this.themeColorRGBA('synthesis-ui-red-01'),
                    type : 'dashed',
                    width: 1.051
                  },
                  label: {
                    show           : true,
                    position       : 'end',
                    formatter      : '{a|Basel Strict Threshold}',
                    width          : 141,
                    height         : 24,
                    color          : this.themeColorRGBA('synthesis-ui-red-01'),
                    fontSize       : 12,
                    backgroundColor: this.themeColorRGBA('synthesis-brand-white'),
                    borderColor    : 'transparent',
                    borderRadius   : 4,
                    padding        : [4, 8, 4, 8],
                    shadowBlur     : 12,
                    shadowSpread   : 0,
                    shadowColor    : 'rgba(110, 142, 223, 0.32)',
                    shadowOffsetX  : 4,
                    shadowOffsetY  : 4,
                    rich           : {
                      a: {
                        color        : this.themeColorRGBA('synthesis-ui-red-01'),
                        fontSize     : 12,
                        align        : 'center',
                        verticalAlign: 'middle',
                        height       : 24
                      }
                    }
                  }
                }
              ]
            },
            z: 2
          },
          // Category name boxes
          {
            name    : 'Category Names',
            type    : 'bar',
            barWidth: '40%',
            data    : this.chartData.values.map((value, index) => ({
              value    : value >= 0 ? 0.1 : -0.1, // Small value to create a box
              itemStyle: {
                color       : this.themeColorRGBA('synthesis-ui-grey-02', 0.8),
                borderRadius: [2, 2, 2, 2]
              }
            })),
            label: {
              show     : true,
              // position: function (params) {
              //   return params.value >= 0 ? 'top' : 'bottom'
              // },
              formatter: function (params) {
                return this.chartData.categories[params.dataIndex]
              }.bind(this),
              color          : 'white',
              fontFamily     : 'Inter',
              letterSpacing  : 0,
              width          : 70,
              fontWeight     : 600,
              fontSize       : 10,
              align          : 'center',
              verticalAlign  : 'middle',
              backgroundColor: this.themeColorRGBA('synthesis-ui-blue-06', 0.8),
              borderColor    : this.themeColorRGBA('synthesis-ui-grey-06'),
              borderWidth    : 0,
              borderRadius   : 2,
              padding        : [4, 8, 4, 8],
              position       : 'outside',
              distance       : -15
              // gap            : 10
            },
            silent        : true,
            z             : 3,
            barGap        : '-100%',
            barCategoryGap: '20%'
          }
        ]
      }
    }
  },
  watch  : {},
  activated () {},
  mounted () {},
  beforeDestroy () {},
  methods: {
    getMaxThresholdValue () {
      return Math.max(...Object.values(this.chartData.thresholds).map(Math.abs))
    },
    getMaxAbsoluteValue () {
      return Math.max(this.getMaxThresholdValue(), ...this.chartData.values.map(v => Math.ceil(Math.abs(v))))
    },
    getMinValue () {
      return -this.getMaxAbsoluteValue()
    },
    getMaxValue () {
      return this.getMaxAbsoluteValue()
    }
  }
}
</script>

<style scoped>
/* Custom styles for the chart container */
</style>
